import type {
  AnalyticsTimeseriesPoint,
  NewAppUsersDay,
} from '@shared/api-client';

export type GrowthMilestone = {
  timestamp: string;
  goalConversions?: number;
  goalCompletions?: number;
  signups: number;
  newPaid: number;
  payments: number;
  /** Provider webhook amounts, in major USD units. Not the historical revenue total. */
  recordedRevenueUsd: number;
};

export type GrowthPoint = AnalyticsTimeseriesPoint & {
  signups: number | null;
  newAppUsers: number | null;
  newPaid: number | null;
  payments: number | null;
  recordedRevenueUsd: number | null;
  newAppUsersIncomplete?: boolean;
};

/** Join on the actual UTC bucket, never array position or a local date label. */
export const buildGrowthPoints = (
  traffic: AnalyticsTimeseriesPoint[],
  milestones: GrowthMilestone[] | undefined,
  paidSince: string | null,
  bucketMinutes: number,
  appUsers?: { days: NewAppUsersDay[]; start: string; end: string },
): GrowthPoint[] => {
  const byTime = new Map(
    milestones?.map((point) => [Date.parse(point.timestamp), point]),
  );
  const coverage = paidSince ? Date.parse(paidSince) : Infinity;
  const goalObserved = milestones !== undefined;
  const daysByDate = new Map(appUsers?.days.map((day) => [day.date, day]));
  return traffic.map((point) => {
    let newAppUsers: number | null = null;
    let newAppUsersIncomplete = false;
    if (appUsers && bucketMinutes >= 1440) {
      const start = Math.max(
        Date.parse(point.timestamp),
        Math.ceil(Date.parse(appUsers.start) / 86_400_000) * 86_400_000,
      );
      const end = Math.min(
        Date.parse(point.timestamp) + bucketMinutes * 60_000,
        Date.parse(appUsers.end),
      );
      if (start < end) {
        newAppUsers = 0;
        for (let date = start; date < end; date += 86_400_000) {
          const day = daysByDate.get(new Date(date).toISOString().slice(0, 10));
          if (day?.count == null) {
            newAppUsers = null;
            break;
          }
          newAppUsers += day.count;
          newAppUsersIncomplete ||= day.incomplete;
        }
      }
    }
    const match = byTime.get(Date.parse(point.timestamp));
    const covered =
      milestones !== undefined &&
      (Date.parse(point.timestamp) + bucketMinutes * 60_000 > coverage ||
        (match?.payments ?? 0) > 0);
    return {
      ...point,
      signups: goalObserved
        ? (match?.goalConversions ?? match?.signups ?? 0)
        : null,
      newAppUsers,
      newAppUsersIncomplete,
      newPaid: covered ? (match?.newPaid ?? 0) : null,
      payments: covered ? (match?.payments ?? 0) : null,
      recordedRevenueUsd: covered ? (match?.recordedRevenueUsd ?? 0) : null,
    };
  });
};

export const formatGrowthDate = (
  timestamp: string,
  hourly: boolean,
  long = false,
): string =>
  new Intl.DateTimeFormat(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(long ? { weekday: 'short' as const } : {}),
    ...(hourly ? { hour: 'numeric' as const, minute: '2-digit' as const } : {}),
  }).format(new Date(timestamp));
